import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Home, CalendarDays, Euro, RefreshCw, Trash2, Upload, Mail, ShoppingBasket, Download, BellRing, Users, BarChart3, Sparkles, QrCode } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ShopAdmin } from "@/components/ShopAdmin";
import { Contacts, downloadCsv } from "@/components/Contacts";
import { PracticalInfoDialog } from "@/components/PracticalInfoDialog";
import { MonthlyStats } from "@/components/MonthlyStats";
import { CleaningSchedule } from "@/components/CleaningSchedule";
import { NotificationsAdmin } from "@/components/NotificationsAdmin";
import { AssignGuest } from "@/components/AssignGuest";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const empty = { title: "", location: "", description: "", price_per_night: 100, bedrooms: 1, bathrooms: 1, max_guests: 2, amenities: "Wifi, Cuisine équipée", images: "" };

export default function HostDashboard() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [form, setForm] = useState(empty);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const load = useCallback(async () => {
    const [l, b] = await Promise.all([api.get("/listings"), api.get("/bookings")]);
    setListings(l.data);
    setBookings(b.data);
  }, [user]);

  useEffect(() => {
    if (loading) return;
    if (!user || !user.role) { navigate("/connexion"); return; }
    if (!["host", "admin"].includes(user.role)) { navigate("/mes-sejours"); return; }
    load();
  }, [user, loading, navigate, load]);

  const create = async () => {
    setBusy(true);
    try {
      await api.post("/listings", {
        ...form,
        price_per_night: Number(form.price_per_night),
        bedrooms: Number(form.bedrooms), bathrooms: Number(form.bathrooms), max_guests: Number(form.max_guests),
        amenities: form.amenities.split(",").map((s) => s.trim()).filter(Boolean),
        images: form.images.split(",").map((s) => s.trim()).filter(Boolean),
      });
      toast.success("Logement ajouté");
      setOpen(false); setForm(empty); load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/listings/${id}`); toast.success("Logement supprimé"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const handleFiles = async (e) => {
    const files = [...e.target.files];
    if (!files.length) return;
    setUploading(true);
    try {
      const urls = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        urls.push(BACKEND + data.url);
      }
      const existing = form.images ? form.images.split(",").map((s) => s.trim()).filter(Boolean) : [];
      setForm((prev) => ({ ...prev, images: [...existing, ...urls].join(", ") }));
      toast.success(`${urls.length} photo(s) ajoutée(s)`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setUploading(false); }
  };

  const syncLodgify = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/lodgify/sync");
      toast.success(`Lodgify synchronisé : ${data.listings} logement(s), ${data.bookings} réservation(s)`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSyncing(false); }
  };

  const sendDigest = async () => {
    try {
      const { data } = await api.post("/admin/digest/send-now");
      toast.success(`Récap envoyé : ${data.arrivals} arrivée(s), ${data.departures} départ(s) → ${data.sent} destinataire(s)`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const pushLodgify = async (id) => {
    try {
      const { data } = await api.post(`/bookings/${id}/push-lodgify`);
      toast.success(`Envoyé à Lodgify (réservation #${data.lodgify_booking_id})`); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const cancelBooking = async (id) => {
    if (!window.confirm("Annuler cette réservation (et dans Lodgify si synchronisée) ?")) return;
    try {
      const { data } = await api.post(`/bookings/${id}/cancel`);
      const parts = ["Annulée"];
      if (data.refund === "refunded") parts.push("client remboursé via Stripe");
      if (data.refund === "error") parts.push("⚠ remboursement Stripe échoué");
      if (data.lodgify === "declined") parts.push("Lodgify mis à jour");
      if (data.lodgify === "error") parts.push("⚠ Lodgify a refusé");
      toast.success(parts.join(" · "));
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const sendReminders = async () => {
    try {
      const { data } = await api.post("/admin/reminders/send-now");
      toast.success(`Rappels veille (${data.date}) : ${data.sent}/${data.arrivals} email(s)${data.sms_enabled ? `, ${data.sms_sent} SMS` : " · SMS inactif (Brevo non configuré)"}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const sendDepartures = async () => {
    try {
      const { data } = await api.post("/admin/departures/send-now");
      toast.success(`Messages départ (${data.date}) : ${data.sent}/${data.departures} email(s)${data.sms_enabled ? `, ${data.sms_sent} SMS` : ""}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const today = new Date().toISOString().slice(0, 10);
  const exportCsv = async (path, params, name) => {
    try { await downloadCsv(path, params, name); toast.success("Export téléchargé"); }
    catch (e) { toast.error("Export impossible"); }
  };

  const previewImages = form.images ? form.images.split(",").map((s) => s.trim()).filter(Boolean) : [];

  const revenue = bookings.filter((b) => b.payment_status === "paid").reduce((s, b) => s + b.total, 0);

  const stats = [
    { icon: Home, label: "Logements", value: listings.length },
    { icon: CalendarDays, label: "Réservations", value: bookings.length },
    { icon: Euro, label: "Revenus encaissés", value: `${revenue} €` },
  ];

  if (!user || !user.role) return <div className="min-h-screen bg-background"><Header /></div>;

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="max-w-6xl mx-auto px-5 md:px-8 py-10">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-serif text-4xl text-olive">Espace Hôte &amp; Équipe</h1>
            <p className="text-muted-foreground mt-1">Gérez vos logements et réservations</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" onClick={sendDigest} className="border-olive text-olive" data-testid="digest-send-button">
              <Mail className="w-4 h-4 mr-2" /> Récap du jour
            </Button>
            <Button variant="outline" onClick={sendReminders} className="border-olive text-olive" data-testid="reminders-send-button">
              <BellRing className="w-4 h-4 mr-2" /> Rappels veille
            </Button>
            <Button variant="outline" onClick={sendDepartures} className="border-olive text-olive" data-testid="departures-send-button">
              <Mail className="w-4 h-4 mr-2" /> Messages départ
            </Button>
            <Button variant="outline" onClick={() => exportCsv("/export/movements.csv", { date: today }, `arrivees-departs-${today}.csv`)} className="border-olive text-olive" data-testid="export-movements-button">
              <Download className="w-4 h-4 mr-2" /> CSV arrivées/départs
            </Button>
            <Button variant="outline" onClick={syncLodgify} disabled={syncing} className="border-olive text-olive" data-testid="lodgify-sync-button">
              <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? "animate-spin" : ""}`} /> {syncing ? "Synchro..." : "Sync Lodgify"}
            </Button>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button className="bg-rose-brand hover:bg-rosebrand-soft text-white" data-testid="add-listing-button"><Plus className="w-4 h-4 mr-2" /> Ajouter</Button>
              </DialogTrigger>
              <DialogContent className="bg-white max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-serif text-2xl text-olive">Nouveau logement</DialogTitle>
                  <DialogDescription>Renseignez les informations et ajoutez des photos de votre logement.</DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                  <div><Label>Titre</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="listing-title-input" /></div>
                  <div><Label>Localisation</Label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="listing-location-input" /></div>
                  <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="listing-desc-input" /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Prix / nuit (€)</Label><Input type="number" value={form.price_per_night} onChange={(e) => setForm({ ...form, price_per_night: e.target.value })} data-testid="listing-price-input" /></div>
                    <div><Label>Voyageurs max</Label><Input type="number" value={form.max_guests} onChange={(e) => setForm({ ...form, max_guests: e.target.value })} /></div>
                    <div><Label>Chambres</Label><Input type="number" value={form.bedrooms} onChange={(e) => setForm({ ...form, bedrooms: e.target.value })} /></div>
                    <div><Label>Salles de bain</Label><Input type="number" value={form.bathrooms} onChange={(e) => setForm({ ...form, bathrooms: e.target.value })} /></div>
                  </div>
                  <div><Label>Équipements (séparés par virgule)</Label><Input value={form.amenities} onChange={(e) => setForm({ ...form, amenities: e.target.value })} /></div>
                  <div>
                    <Label>Photos (depuis votre ordinateur)</Label>
                    <label className="mt-1 flex items-center justify-center gap-2 h-11 rounded-xl border border-dashed border-olive text-olive text-sm cursor-pointer hover:bg-secondary transition-colors" data-testid="photo-upload-label">
                      <Upload className="w-4 h-4" /> {uploading ? "Envoi en cours..." : "Choisir des images"}
                      <input type="file" accept="image/*" multiple onChange={handleFiles} className="hidden" data-testid="listing-photo-input" />
                    </label>
                    {previewImages.length > 0 && (
                      <div className="flex gap-2 flex-wrap mt-2">
                        {previewImages.map((src, i) => (
                          <img key={i} src={src} alt="" className="w-16 h-16 object-cover rounded-lg border border-border" />
                        ))}
                      </div>
                    )}
                  </div>
                  <div><Label>Ou images par URL (séparées par virgule)</Label><Input value={form.images} onChange={(e) => setForm({ ...form, images: e.target.value })} placeholder="https://..." /></div>
                </div>
                <DialogFooter>
                  <Button onClick={create} disabled={busy} className="bg-olive text-white" data-testid="save-listing-button">{busy ? "..." : "Enregistrer"}</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-8">
          {stats.map((s) => (
            <div key={s.label} className="p-5 rounded-2xl bg-white border border-border" data-testid={`stat-${s.label}`}>
              <s.icon className="w-6 h-6 text-rose-brand" />
              <p className="text-2xl font-bold text-olive mt-3">{s.value}</p>
              <p className="text-sm text-muted-foreground">{s.label}</p>
            </div>
          ))}
        </div>

        <Tabs defaultValue="logements" className="mt-10">
          <TabsList className="bg-secondary rounded-full h-11 p-1 flex w-full sm:w-auto overflow-x-auto justify-start">
            <TabsTrigger value="logements" className="rounded-full px-3 sm:px-5 shrink-0 data-[state=active]:bg-white data-[state=active]:text-olive" data-testid="tab-logements"><Home className="w-4 h-4 sm:mr-2" /> <span className="hidden sm:inline">Logements &amp; réservations</span><span className="sm:hidden ml-1">Logements</span></TabsTrigger>
            <TabsTrigger value="drive" className="rounded-full px-3 sm:px-5 shrink-0 data-[state=active]:bg-white data-[state=active]:text-olive" data-testid="tab-drive"><ShoppingBasket className="w-4 h-4 sm:mr-2" /> <span className="hidden sm:inline">Course en ligne</span><span className="sm:hidden ml-1">Drive</span></TabsTrigger>
            <TabsTrigger value="contacts" className="rounded-full px-3 sm:px-5 shrink-0 data-[state=active]:bg-white data-[state=active]:text-olive" data-testid="tab-contacts"><Users className="w-4 h-4 mr-1 sm:mr-2" /> Contacts</TabsTrigger>
            <TabsTrigger value="menage" className="rounded-full px-3 sm:px-5 shrink-0 data-[state=active]:bg-white data-[state=active]:text-olive" data-testid="tab-menage"><Sparkles className="w-4 h-4 mr-1 sm:mr-2" /> Ménage</TabsTrigger>
            <TabsTrigger value="stats" className="rounded-full px-3 sm:px-5 shrink-0 data-[state=active]:bg-white data-[state=active]:text-olive" data-testid="tab-stats"><BarChart3 className="w-4 h-4 mr-1 sm:mr-2" /> Statistiques</TabsTrigger>
            <TabsTrigger value="notifs" className="rounded-full px-3 sm:px-5 shrink-0 data-[state=active]:bg-white data-[state=active]:text-olive" data-testid="tab-notifs"><BellRing className="w-4 h-4 mr-1 sm:mr-2" /> Notifications</TabsTrigger>
          </TabsList>
          <TabsContent value="notifs" className="mt-6"><NotificationsAdmin /></TabsContent>
          <TabsContent value="menage" className="mt-6"><CleaningSchedule /></TabsContent>
          <TabsContent value="stats" className="mt-6"><MonthlyStats /></TabsContent>
          <TabsContent value="contacts" className="mt-6"><Contacts /></TabsContent>
          <TabsContent value="drive" className="mt-6">
            <div className="flex justify-end mb-4">
              <Button variant="outline" onClick={() => exportCsv("/export/orders.csv", {}, "commandes.csv")} className="border-olive text-olive" data-testid="export-orders-button">
                <Download className="w-4 h-4 mr-2" /> CSV commandes à préparer
              </Button>
            </div>
            <ShopAdmin />
          </TabsContent>
          <TabsContent value="logements">
        <h2 className="font-serif text-2xl text-olive mt-6 mb-4">Mes logements</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {listings.map((l) => (
            <div key={l.listing_id} className="flex gap-3 p-3 rounded-2xl bg-white border border-border" data-testid={`host-listing-${l.listing_id}`}>
              <img src={l.images?.[0]} alt="" className="w-24 h-24 object-cover rounded-xl" />
              <div className="flex-1">
                <h3 className="font-serif text-lg text-olive">{l.title}</h3>
                <p className="text-sm text-muted-foreground">{l.location}</p>
                <p className="text-sm font-bold text-olive mt-1">{l.price_per_night} € / nuit</p>
                <PracticalInfoDialog key={JSON.stringify(l.practical || {})} listing={l} onSaved={load} />
                <a href={`${process.env.REACT_APP_BACKEND_URL}/api/listings/${l.listing_id}/qr.png?size=16`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs mt-1 ml-3 text-olive hover:underline" data-testid={`qr-link-${l.listing_id}`}>
                  <QrCode className="w-3.5 h-3.5" /> QR code
                </a>
                <a href={`${process.env.REACT_APP_BACKEND_URL}/api/listings/${l.listing_id}/poster`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs mt-1 ml-3 text-rose-brand hover:underline" data-testid={`poster-link-${l.listing_id}`}>
                  <QrCode className="w-3.5 h-3.5" /> Affiche A5 à imprimer
                </a>
              </div>
              <button onClick={() => remove(l.listing_id)} className="self-start text-muted-foreground hover:text-destructive" data-testid={`delete-listing-${l.listing_id}`}><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>

        <h2 className="font-serif text-2xl text-olive mt-10 mb-4">Réservations reçues</h2>
        <div className="space-y-3">
          {bookings.length === 0 && <p className="text-muted-foreground">Aucune réservation.</p>}
          {bookings.map((b) => (
            <div key={b.booking_id} className="flex items-center justify-between p-4 rounded-xl bg-white border border-border" data-testid={`host-booking-${b.booking_id}`}>
              <div>
                <p className="font-semibold text-olive">{b.listing_title}</p>
                <p className="text-sm text-muted-foreground">{b.traveler_name} · {b.check_in} → {b.check_out}</p>
                {b.status !== "cancelled" && <div className="mt-1"><AssignGuest booking={b} onChanged={load} /></div>}
                {b.source !== "lodgify" && (
                  <p className="text-xs mt-1" data-testid={`lodgify-push-${b.booking_id}`}>
                    {b.lodgify_booking_id ? <span className="text-olive">✓ Synchronisé Lodgify #{b.lodgify_booking_id}</span>
                      : b.lodgify_push === "error" ? <span className="text-destructive">Erreur Lodgify : {b.lodgify_push_error}</span>
                      : <span className="text-muted-foreground">Non envoyé à Lodgify</span>}
                    {!b.lodgify_booking_id && b.payment_status === "paid" && (
                      <button onClick={() => pushLodgify(b.booking_id)} className="ml-2 text-rose-brand font-semibold hover:underline" data-testid={`push-lodgify-${b.booking_id}`}>Envoyer</button>
                    )}
                  </p>
                )}
              </div>
              <div className="text-right">
                <p className="font-bold text-olive">{b.total} €</p>
                <Badge className={b.status === "cancelled" ? "bg-secondary text-muted-foreground" : b.payment_status === "paid" ? "bg-olive text-white" : "bg-rosebrand-pale text-rose-brand"}>
                  {b.status === "cancelled" ? (b.payment_status === "refunded" ? "Annulée · Remboursée" : "Annulée") : b.payment_status === "paid" ? "Payé" : "En attente"}
                </Badge>
                {b.status !== "cancelled" && b.source !== "lodgify" && (
                  <button onClick={() => cancelBooking(b.booking_id)} className="block ml-auto mt-1 text-xs text-muted-foreground hover:text-destructive underline" data-testid={`host-cancel-${b.booking_id}`}>Annuler</button>
                )}
              </div>
            </div>
          ))}
        </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
